/**
 * AgentTimeline — the center column of the AgentWindow.
 *
 * Renders the conversation (UIMessage[]) via the existing ChatMessage
 * renderer, with the active run's machinery (status ladder, plan, tools,
 * sub-agents, questions) shown as a live block beneath the latest message.
 * Completed runs collapse into the Workbench activity feed.
 */

import { useEffect, useMemo, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@flowstate/utils";
import { CortexInitAnimation } from "@/features/ai-agent/components/CortexInitAnimation";
import ChatMessage from "@/features/ai-agent/components/ChatMessage";
import type { ExtendedMessage } from "@/features/ai-agent/types/chat";
import type { UIMessage } from "@flowstate/ai/react";
import {
  runToOrchestratorMeta,
  type CortexTimeline,
  type TimelineRun,
} from "../engine/cortexStreamReducer";
import { RunEventRow } from "./timeline/RunEventRow";
import { agentBranding } from "@/features/ai-agent/config/agentBranding";

interface AgentTimelineProps {
  messages: UIMessage[];
  timeline: CortexTimeline;
  runs: TimelineRun[];
  isLoading: boolean;
  onAnswer: (runId: string, opts: { approved?: boolean; value?: string }) => void;
  onFollowUpClick?: (text: string) => void;
  /** Render compact (drawer) vs roomy (fullscreen). */
  compact?: boolean;
}

function messageText(m: UIMessage): string {
  return m.parts
    .map((p) =>
      p.type === "text" && "content" in p ? String((p as { content: string }).content) : "",
    )
    .join("");
}

function toExtended(m: UIMessage, run?: TimelineRun, isLoading?: boolean): ExtendedMessage {
  const isAssistant = m.role === "assistant";
  const isStreaming = isAssistant && isLoading;
  return {
    id: m.id,
    role: m.role as "user" | "assistant",
    content: messageText(m),
    timestamp: m.createdAt ?? new Date(),
    isTyping: isStreaming,
    orchestratorMeta: run && isAssistant ? runToOrchestratorMeta(run) : undefined,
    specialistData: run?.specialistData?.at(-1),
  } as unknown as ExtendedMessage;
}

/** Events worth rendering under the latest message while a run is live. */
function liveEvents(run: TimelineRun) {
  return run.events.filter((e) => e.kind !== "text" && e.kind !== "specialist_data");
}

export function AgentTimeline({
  messages,
  timeline,
  runs,
  isLoading,
  onAnswer,
  onFollowUpClick,
  compact,
}: AgentTimelineProps) {
  const endRef = useRef<HTMLDivElement>(null);

  const lastMessage = messages.at(-1);
  const activeRun = runs.find(
    (r) => r.lifecycle === "running" || r.lifecycle === "awaiting" || r.lifecycle === "awaiting_input",
  );
  // Attach the active run's orchestration meta to the last assistant message.
  const lastRun = runs.at(-1);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, activeRun?.events.length]);

  const rendered = useMemo(
    () =>
      messages.map((m) => {
        const isLast = m.id === lastMessage?.id;
        const run = isLast && m.role === "assistant" ? lastRun : undefined;
        return (
          <div key={m.id} className={cn(compact ? "px-3" : "px-6", "py-1")}>
            <ChatMessage
              message={toExtended(m, run, isLast && isLoading)}
              isLatest={isLast}
              onFollowUpClick={onFollowUpClick}
            />
          </div>
        );
      }),
    [messages, lastMessage?.id, lastRun, isLoading, compact, onFollowUpClick],
  );

  const liveRunEvents = activeRun ? liveEvents(activeRun) : [];
  // An active run with no assistant text yet — show its machinery inline so
  // the user sees the plan/tools/question even before the first token.
  const showRunBlock =
    activeRun &&
    (lastMessage?.role !== "assistant" || !messageText(lastMessage ?? ({} as UIMessage)));

  return (
    <ScrollArea className="flex-1 min-h-0">
      <div className={cn("py-4 space-y-1", !compact && "max-w-3xl mx-auto")}>
        {messages.length === 0 && !activeRun && (
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <CortexInitAnimation variant="bubble" className="w-16 h-16" />
            <div>
              <p className="text-sm font-medium">{agentBranding.name} is ready</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                Ask a question, or start a work session to give {agentBranding.name} a
                longer task with its own workspace.
              </p>
            </div>
          </div>
        )}
        {rendered}

        {/* Live run machinery — status, plan, tools, question */}
        {showRunBlock && liveRunEvents.length > 0 && (
          <div className={cn(compact ? "px-3" : "px-6", "py-2 space-y-1.5")}>
            {liveRunEvents.map((e) => (
              <RunEventRow
                key={`${e.at}-${e.kind}-${e.seq ?? 0}`}
                run={activeRun}
                event={e}
                onAnswer={onAnswer}
              />
            ))}
          </div>
        )}

        {/* Pending question for a run whose stream already closed */}
        {!activeRun &&
          runs
            .filter((r) => r.lifecycle === "awaiting_input" && r.question)
            .map((r) => (
              <div key={`q-${r.id}`} className={cn(compact ? "px-3" : "px-6", "py-1")}>
                {r.events
                  .filter((e) => e.kind === "question")
                  .map((e) => (
                    <RunEventRow key={`${e.at}-${e.seq ?? 0}`} run={r} event={e} onAnswer={onAnswer} />
                  ))}
              </div>
            ))}

        <div ref={endRef} />
      </div>
    </ScrollArea>
  );
}
